import { ref } from '@vue/composition-api'
import UserPerformanceSummaryApi from '@/api/v2/eod-closing/UserPerformanceSummary'

// クロージング日計画面のユーザー別実績（受領商品ごとの数量・販売価格合計、労働時間）を
// ユーザーIDをキーにしたキャッシュで保持する composable（案1: 専用ユーザー単位エンドポイント方式）。
//
// 動作:
// - クリック都度で1ユーザー分のみ API を叩く
// - 既に取得済みのユーザーはキャッシュから即時 return
// - 日付が変わったらキャッシュ全クリア
// - composable をコンポーネント内 setup() で呼ぶことで、コンポーネントのライフサイクルに紐づく
//   別画面に遷移してアンマウントされればキャッシュは自動破棄される
//
// 将来「キャッシュ機構を強化したい」となった場合は TanStack Vue Query 等への移行を検討。
// 詳細は steering の design.md「キャッシュ実装の選択肢」メモ参照。
export default () => {
  const isLoading = ref(false)
  const userSummariesMap = ref({})
  const lastFetchedDate = ref(null)

  const clearCache = () => {
    userSummariesMap.value = {}
  }

  // 特定ユーザーのキャッシュのみ破棄する（勤務時間編集後の再取得などに使用）
  const invalidateUser = userId => {
    if (!userSummariesMap.value[userId]) return
    const { [userId]: _removed, ...rest } = userSummariesMap.value
    userSummariesMap.value = rest
  }

  const fetch = async (date, userId) => {
    if (!date || !userId) return

    // 日付変更時はキャッシュ全クリア
    if (lastFetchedDate.value && lastFetchedDate.value !== date) {
      clearCache()
    }
    lastFetchedDate.value = date

    if (userSummariesMap.value[userId]) return

    isLoading.value = true
    try {
      const res = await UserPerformanceSummaryApi.getUserPerformanceSummary({ date, userId })
      if (res?.data?.status === 'success') {
        const data = res.data.data || {}
        userSummariesMap.value = {
          ...userSummariesMap.value,
          [userId]: {
            userName: data.userName || '',
            items: data.items || [],
            workingMinutes: Number(data.workingMinutes || 0),
          },
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  const summaryForUser = userId => {
    const found = userSummariesMap.value[userId]
    if (!found) {
      return {
        items: [], totalCount: 0, totalPrice: 0, workingMinutes: 0,
      }
    }

    const items = found.items || []

    return {
      items,
      totalCount: items.reduce((acc, item) => acc + Number(item.count || 0), 0),
      totalPrice: items.reduce((acc, item) => acc + Number(item.totalPrice || 0), 0),
      workingMinutes: found.workingMinutes || 0,
    }
  }

  return {
    isLoading,
    fetch,
    summaryForUser,
    clearCache,
    invalidateUser,
  }
}
